/**
 * Клиент управления доступом (для экрана «Доступы»): пользователи, приглашения,
 * пароли, типы работника, роли. Все запросы — под правом «управление персоналом».
 */
import { authHeaders, notifyUnauthorized } from './session';
import type { RolePermissions } from './permissions';

const BASE = '/api';

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const hasBody = body !== undefined;
  const res = await fetch(`${BASE}${path}`, {
    method,
    // Content-Type: application/json ставим ТОЛЬКО когда реально шлём тело. Иначе на POST без
    // тела («Подтвердить»/«Отклонить» регистрацию) Fastify видит заголовок json + пустое тело
    // и отвечает 400 «Bad Request» — кнопки не срабатывали.
    headers: { Accept: 'application/json', ...(hasBody ? { 'Content-Type': 'application/json' } : {}), ...authHeaders() },
    body: hasBody ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) notifyUnauthorized();
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as any)?.error || `HTTP ${res.status}`);
  return json as T;
}

export interface AdminUser {
  id: string;
  kind: 'bitrix' | 'local';
  bitrix_user_id: number | null;
  login: string | null;
  full_name: string;
  email: string | null;
  access_role: string | null;
  worker_type: string | null;
  employee_id: string | null;
  city_ids: string[];
  is_active: boolean;
  is_approved?: boolean;
  notify_registrations?: boolean;
  telegram_linked?: boolean;
}

export interface WorkerType { id: string; name: string; icon: string | null; sort_order: number; is_system: boolean }
export interface AccessRole {
  id: string; name: string; description: string | null; permissions: RolePermissions; is_system: boolean; sort_order: number;
  /** Слова из должности Битрикса, по которым роль выдаётся сама при первом входе. Пусто/нет — только вручную. */
  bitrix_keywords?: string[] | null;
}

export interface InviteInput {
  email?: string;
  full_name?: string;
  access_role?: string;
  worker_type?: string | null;
  employee_id?: string | null;
  city_ids?: string[];
  temp_password?: string;
}

export const listUsers = () => req<AdminUser[]>('GET', '/users');
export const updateUser = (id: string, patch: Partial<AdminUser>) => req<AdminUser>('PUT', `/users/${id}`, patch);
export const deleteUser = (id: string) => req<{ ok: true }>('DELETE', `/users/${id}`);
export const setUserPassword = (id: string, password: string) => req<{ ok: true }>('POST', `/users/${id}/set-password`, { password });
export const createInvite = (input: InviteInput) => req<{ token: string; temp_password?: string }>('POST', '/invites', input);

// ── «Замок 1»: очередь подтверждения регистраций (Руководство/супер-админ) ──
/** Список зарегистрировавшихся по общей ссылке, ожидающих подтверждения. */
export const listPending = () => req<AdminUser[]>('GET', '/users/pending');
/** Подтвердить регистрацию — открыть человеку доступ. */
export const approveUser = (id: string) => req<AdminUser>('POST', `/users/${id}/approve`);
/** Отклонить регистрацию — удалить заявку. */
export const rejectUser = (id: string) => req<{ ok: true }>('POST', `/users/${id}/reject`);

export const listWorkerTypes = () => req<WorkerType[]>('GET', '/worker_types');
export const listAccessRoles = () => req<AccessRole[]>('GET', '/access_roles');

/** Данные пользователя Битрикса по ID (для ручного заведения сотрудника супер-админом). */
export interface BitrixUserLookup { bitrix_user_id: number; full_name: string; position: string; email: string; phone: string }
export const lookupBitrixUser = (id: number | string) =>
  req<BitrixUserLookup>('GET', `/bitrix/user/${encodeURIComponent(String(id))}`);

/** Сохранить права/имя доступ-роли (пишется в access_roles.permissions JSONB). Тот же путь
 *  заводит и НОВУЮ роль — id для новой генерирует клиент (см. createAccessRole). */
export const updateAccessRole = (
  id: string,
  patch: { name?: string; description?: string | null; permissions: RolePermissions; bitrix_keywords?: string[] },
) => req<AccessRole>('PUT', `/access_roles/${id}`, patch);

/** Завести свою роль доступа (не из 8 системных) — с нуля, супер-админ задаёт права галочками после. */
export const createAccessRole = (id: string, name: string, bitrix_keywords: string[] = []) =>
  req<AccessRole>('PUT', `/access_roles/${id}`, { name, permissions: {}, bitrix_keywords });

/** Удалить свою роль (нельзя удалить, пока она кому-то назначена — сервер проверит). */
export const deleteAccessRole = (id: string) => req<{ ok: true }>('DELETE', `/access_roles/${id}`);

/** Создать/переименовать тип работника (линейная должность: название+иконка). */
export const upsertWorkerType = (id: string, patch: { name: string; icon?: string | null; sort_order?: number }) =>
  req<WorkerType>('PUT', `/worker_types/${id}`, patch);

/** Удалить тип работника (только несистемный). */
export const deleteWorkerType = (id: string) => req<{ ok: true }>('DELETE', `/worker_types/${id}`);

/**
 * Ссылка-приглашение для ЛИНЕЙНОГО сотрудника (без Битрикса).
 * Содержит «пропуск» через шлюз VibeCode (?s=<shortcode>) — иначе охрана «Защищённый
 * сервер» не пустит. Код инвайта дублируем в query и в #-хвост: если шлюз срежет query,
 * хвост переживёт редирект (App.tsx читает и оттуда). Shortcode обновлять при перевыпуске
 * (он же в index.html).
 */
const GATEWAY_SHORTCODE = import.meta.env.VITE_GATEWAY_SHORTCODE || 'HdmHWNfo';
export function inviteLink(token: string): string {
  const t = encodeURIComponent(token);
  return `${window.location.origin}/placement?s=${GATEWAY_SHORTCODE}&invite=${t}#invite=${t}`;
}

/** Общая (многоразовая) ссылка регистрации — одна на всех линейных. */
export async function getRegistrationLink(): Promise<string> {
  const { token } = await req<{ token: string }>('GET', '/registration-link');
  return inviteLink(token);
}

// ── Настройки предброни (только руководитель + супер-админ) ──────────────
/** Поля предброни из синглтона настроек (могут отсутствовать → фронт показывает дефолты). */
export interface PrebookingSettings {
  prebooking_enabled?: boolean;
  prebooking_hold_minutes_b2c?: number;
  prebooking_hold_minutes_b2b?: number;
}

/** Прочитать текущий синглтон настроек (нужны поля предброни). GET не требует особых прав. */
export const getSettings = () => req<Record<string, unknown> | null>('GET', '/settings');

/** Сохранить только поля предброни (сервер мёржит их в синглтон, права — canManageBranches). */
export const savePrebookingSettings = (patch: PrebookingSettings) =>
  req<Record<string, unknown>>('PUT', '/settings/prebooking', patch);

// ── Справочник ставок на расчёт зарплаты (защищённый экран, право pay_rates) ──
// host2 — второй ведущий на той же игре (решение 02.11.2026: у двух ведущих на одной игре
// ставка разная по правилу, не только по личному уровню 1/2/3) — см. комментарий в payroll/calc.ts.
export type RateRole = 'host' | 'host2' | 'dj' | 'sovedushiy' | 'hostes';

export interface PayRate {
  id: string;
  branch_id: string;          // id филиала (branches.id) — у каждого свои ставки
  format_id: string;          // id из game_formats
  role: RateRole;
  level: 1 | 2 | 3;
  duration_min: number;       // минут (60/90/120/… — для «Ключа» до 360)
  people_min: number;
  people_max: number | null;  // null = без верхней границы
  amount: number;
}

/** 7 системных ключей (заданы заказчиком, считаются по своим правилам — см. payroll/calc.ts). */
export type PayAddonKey = 'night' | 'english' | 'banquet' | 'teamlead' | 'distance_pct' | 'no_catering' | 'waiting';
export const FIXED_ADDON_KEYS: readonly PayAddonKey[] = ['night', 'english', 'banquet', 'teamlead', 'distance_pct', 'no_catering', 'waiting'];
export interface PayAddon {
  key: string;
  amount: number;
  /** Название доплаты — только у СВОИХ (не системных) доплат, которые заводит сам пользователь
   *  (03.11.2026). У системных 7 название берётся из ADDON_DEFS в PayRatesPage.tsx. */
  label?: string;
}

/** Название + ключ своей доплаты (без суммы) — отдаётся ЛЮБОМУ сотруднику, чтобы можно было
 *  отметить доплату конкретному человеку на игре (DealEditor → PeopleEdit), не видя её цену. */
export interface CustomAddonLabel { key: string; label: string }
export const getCustomAddonLabels = () => req<CustomAddonLabel[]>('GET', '/pay_rates/custom_addon_labels');

export interface PayRatesData {
  rates: PayRate[];
  addons: PayAddon[];
}

export const getPayRates = () => req<PayRatesData>('GET', '/pay_rates');
export const savePayRates = (data: PayRatesData) => req<PayRatesData>('PUT', '/pay_rates', data);

/** Расчёт зарплаты за игру — сервер считает сам, сам справочник ставок наружу не отдаёт.
 *  Обычный сотрудник получает только СВОЮ строку, у кого право pay_rates — весь состав. */
export interface EmployeePayResult {
  employee_id: string;
  role_key: string;
  pay_role: RateRole | null;
  level: number | null;
  ok: boolean;
  reason?: string;
  base?: number;
  addons?: { key: string; label: string; amount: number }[];
  total?: number;
}
export const getDealPayroll = (dealId: string) => req<EmployeePayResult[]>('GET', `/deals/${dealId}/payroll`);

/**
 * Игры за период, где я — ответственный с галочкой «Агентская продажа». Агентские не зависят
 * от состава смены, поэтому такая игра может не попасть в обычный список «Мои игры»
 * (построен по составу/бригаде) — без этого запроса сотрудник её там не увидит вообще.
 */
export const getMyAgentSaleDealIds = (from: number, to: number) =>
  req<string[]>('GET', `/payroll/my-agent-sales?from=${from}&to=${to}`);

/** Отчёт по зарплате за период (фаза 6) — по сотруднику: сумма, кол-во игр, список игр. */
export interface PayrollReportItem {
  deal_id: string; number: string | null; date: number;
  role_key: string; level: number | null; ok: boolean;
  base?: number; addons?: { key: string; label: string; amount: number }[]; total?: number; reason?: string;
}
export interface PayrollReportRow {
  employee_id: string; full_name: string; games: number; unresolved: number; total: number; items: PayrollReportItem[];
}
export const getPayrollReport = (from: number, to: number, branchId: string | 'all' = 'all') =>
  req<PayrollReportRow[]>('GET', `/payroll/report?from=${from}&to=${to}&branch_id=${encodeURIComponent(branchId)}`);

// ── Колокольчик уведомлений (дни рождения, чат 02.11.2026) ──────────────
export interface NotificationItem {
  id: string;
  kind: 'fill_birthday' | 'birthday_soon';
  text: string;
  created_at: number;
}
export const getNotifications = () => req<NotificationItem[]>('GET', '/notifications');
export const markNotificationRead = (id: string) => req<{ ok: true }>('PUT', `/notifications/${id}/read`);
